/**
 * DayPlanCard — displays the full nutrition plan for one weekday.
 *
 * Structure (per reference site):
 *   1. Day name (large) + trainingBadge chip (conditional) + FOCO label + foco text
 *   2. Big kcal card: targetKcal + "objetivo del día" + 3 macro columns + MacroBar
 *   3. "COMIDAS DEL DÍA" — vertical timeline of meals (each: label + items + kcal)
 *
 * Security: all LLM/DB strings rendered via React text bindings — no dangerouslySetInnerHTML.
 * Accessibility: role=tabpanel, semantic section/h headings.
 */

import type { DayPlan, Meal } from "@autodieta/shared";
import { formatKcal, formatMacro } from "../utils/format.js";
import { MacroBar } from "./MacroBar.js";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface DayPlanCardProps {
  day: DayPlan;
  /** Human-readable day label, e.g. "Lunes", "Martes". */
  dayLabel: string;
  /** DOM id for the tabpanel — must match the tab's aria-controls. */
  panelId: string;
}

// ---------------------------------------------------------------------------
// DayPlanCard
// ---------------------------------------------------------------------------

export function DayPlanCard({ day, dayLabel, panelId }: DayPlanCardProps): JSX.Element {
  const { trainingBadge, foco, targets, meals, totals, restrictionFlag } = day;
  const { targetKcal, macros } = targets;

  return (
    <section
      id={panelId}
      role="tabpanel"
      aria-label={`Plan nutricional del ${dayLabel}`}
      className="flex flex-col gap-4"
    >
      {/* Day header: name + badge + foco */}
      <DayHeader dayLabel={dayLabel} trainingBadge={trainingBadge} foco={foco} />

      {/* Restriction warning banner — surfaced only when the engine flagged the day */}
      {restrictionFlag === true ? <RestrictionBanner /> : null}

      {/* Kcal + macros card */}
      <MacroSummaryCard targetKcal={targetKcal} macros={macros} totals={totals} />

      {/* Meals timeline */}
      <MealsTimeline meals={meals} />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Day header
// ---------------------------------------------------------------------------

interface DayHeaderProps {
  dayLabel: string;
  trainingBadge: string | undefined;
  foco: string;
}

function DayHeader({ dayLabel, trainingBadge, foco }: DayHeaderProps): JSX.Element {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-2xl font-bold text-zinc-100">{dayLabel}</h2>
        {trainingBadge !== undefined ? (
          <span className="rounded-full bg-orange-900/40 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-orange-400">
            {trainingBadge}
          </span>
        ) : null}
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs font-bold uppercase tracking-widest text-zinc-500">FOCO</span>
        <span className="text-sm text-zinc-300">{foco}</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Restriction warning banner (F6.2 — restrictionFlag surfaced to the user)
// ---------------------------------------------------------------------------

/**
 * Accessible warning shown when DayPlan.restrictionFlag is true: the engine
 * dropped or substituted a restricted food for this specific day.
 *
 * Accessibility:
 *   - role="alert" + aria-live="assertive" so screen readers announce it.
 *   - Not color-only: an explicit "Atención" prefix + warning icon carry the
 *     meaning beyond the amber styling.
 *   - All text via React bindings — no dangerouslySetInnerHTML.
 */
function RestrictionBanner(): JSX.Element {
  return (
    <div
      role="alert"
      aria-live="assertive"
      className="flex items-start gap-2 rounded-lg border border-amber-700/60 bg-amber-950/40 p-3 text-sm text-amber-200"
    >
      <span aria-hidden="true" className="mt-0.5 shrink-0 font-bold">
        ⚠
      </span>
      <p>
        <span className="font-semibold">Atención:</span> se ajustó este día por una
        restricción — revisá las sustituciones en las comidas.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Macro summary card
// ---------------------------------------------------------------------------

interface MacroSummaryCardProps {
  targetKcal: number;
  macros: DayPlan["targets"]["macros"];
  totals: DayPlan["totals"];
}

function MacroSummaryCard({ targetKcal, macros }: MacroSummaryCardProps): JSX.Element {
  return (
    <div className="rounded-xl border border-zinc-700 bg-zinc-900 p-4">
      {/* kcal headline */}
      <div className="mb-4 flex flex-col items-center gap-0.5">
        <span className="text-4xl font-black text-zinc-100">{formatKcal(targetKcal)}</span>
        <span className="text-xs font-medium uppercase tracking-wide text-zinc-500">
          objetivo del día
        </span>
      </div>

      {/* 3 macro columns */}
      <div className="mb-4 grid grid-cols-3 gap-2 text-center">
        <MacroColumn
          label="Proteína"
          grams={macros.proteinG}
          pct={macros.proteinPct}
        />
        <MacroColumn
          label="Carbos"
          grams={macros.carbG}
          pct={macros.carbPct}
        />
        <MacroColumn
          label="Grasas"
          grams={macros.fatG}
          pct={macros.fatPct}
        />
      </div>

      {/* Stacked macro bar */}
      <MacroBar macros={macros} />
    </div>
  );
}

interface MacroColumnProps {
  label: string;
  grams: number;
  pct: number;
}

function MacroColumn({ label, grams, pct }: MacroColumnProps): JSX.Element {
  return (
    <div className="flex flex-col items-center gap-0.5">
      <span className="text-xs text-zinc-500">{label}</span>
      <span className="text-lg font-bold text-zinc-100">{formatMacro(grams)} g</span>
      <span className="text-xs text-zinc-400">{formatMacro(pct)}%</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Meals timeline
// ---------------------------------------------------------------------------

interface MealsTimelineProps {
  meals: Meal[];
}

function MealsTimeline({ meals }: MealsTimelineProps): JSX.Element {
  return (
    <section aria-label="Comidas del día">
      <h3 className="mb-3 text-xs font-bold uppercase tracking-widest text-zinc-400">
        COMIDAS DEL DÍA
      </h3>
      <ol className="flex flex-col gap-3" aria-label="Lista de comidas">
        {meals.map((meal, i) => (
          <MealTimelineItem key={`${meal.label}-${i}`} meal={meal} />
        ))}
      </ol>
    </section>
  );
}

interface MealTimelineItemProps {
  meal: Meal;
}

function MealTimelineItem({ meal }: MealTimelineItemProps): JSX.Element {
  const mealKcal = meal.items.reduce((sum, item) => sum + item.kcal, 0);

  return (
    <li className="relative flex gap-3">
      {/* Bullet dot */}
      <div className="flex flex-col items-center">
        <span
          className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full bg-orange-500"
          aria-hidden="true"
        />
        <span className="mt-1 w-px flex-1 bg-zinc-800" aria-hidden="true" />
      </div>

      {/* Content */}
      <div className="flex-1 pb-3">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-semibold text-zinc-200">{meal.label}</span>
          <span className="shrink-0 text-xs font-medium text-zinc-400">
            {formatKcal(mealKcal)} kcal
          </span>
        </div>
        <ul className="mt-1 flex flex-col gap-1" aria-label={`Alimentos de ${meal.label}`}>
          {meal.items.map((item, j) => (
            <li key={`${item.foodName}-${j}`} className="text-xs text-zinc-500">
              {item.foodName}{" "}
              <span className="text-zinc-600">({formatMacro(item.grams)} g)</span>
            </li>
          ))}
        </ul>
      </div>
    </li>
  );
}
